import React, { useRef } from 'react';
import { Animated, ScrollView } from 'react-native';
import Column from 'src/components/10-atoms/layout/Column';
import Row from 'src/components/10-atoms/layout/Row';
import Pressable from 'src/components/10-atoms/pressables/Pressable';
import Typography from 'src/components/10-atoms/typography/Typography';
import { TypographyVariant } from 'src/customizations/themes/10-parts/typography';
import useTheme from 'src/customizations/themes/hooks/useTheme';
import createThemedStyle from 'src/customizations/themes/utils/createThemedStyles';
import useEffectWithCtx from 'src/hooks/useEffectUtils/useEffectWithCtx';
import useI18n from 'src/modules/translations/hooks/useI18n';
/** ponytail: fixed maxHeight; bump if banner content gets clipped. */
const BANNER_EXPAND_MAX_HEIGHT = 120;
const TopicNotesBanner = ({ loading, notes }) => {
    const { translate } = useI18n();
    const [{ theme, styles }] = useTheme(stylesGenerator);
    const expandAnim = useRef(new Animated.Value(0)).current;
    const hasNotes = (notes?.totalCount ?? 0) > 0;
    useEffectWithCtx(ctx => {
        if (ctx.loading || !ctx.hasNotes) {
            ctx.expandAnim.stopAnimation();
            ctx.expandAnim.setValue(0);
            return;
        }
        ctx.expandAnim.setValue(0);
        Animated.timing(ctx.expandAnim, {
            toValue: 1,
            duration: 800,
            useNativeDriver: false,
        }).start();
    }, { loading, hasNotes, expandAnim }, [loading, hasNotes]);
    if (loading || !hasNotes) {
        return null;
    }
    return (React.createElement(Animated.View, { style: {
            maxHeight: expandAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [0, BANNER_EXPAND_MAX_HEIGHT],
            }),
            opacity: expandAnim,
            overflow: 'hidden',
        } },
        React.createElement(Column, { style: styles.banner, spacing: theme.spacing.standard.xs },
            React.createElement(Typography, { variant: TypographyVariant.CAPTION }, translate('Notes for this topic')),
            React.createElement(ScrollView, { horizontal: true, showsHorizontalScrollIndicator: false, contentContainerStyle: styles.scrollContent },
                React.createElement(Row, { spacing: theme.spacing.standard.sm }, (notes?.list ?? []).map(note => (React.createElement(Pressable, { key: note.id, to: { screen: 'EP_NoteViewer', params: { noteID: note.id } } },
                    React.createElement(Column, { style: styles.noteCard },
                        React.createElement(Typography, { variant: TypographyVariant.BODY, numberOfLines: 2 }, note.data.title))))))))));
};
const stylesGenerator = createThemedStyle(({ theme }) => ({
    banner: {
        backgroundColor: theme.palette.background.message,
        paddingHorizontal: theme.spacing.page,
        paddingTop: theme.spacing.standard.xs,
        paddingBottom: theme.spacing.standard.sm,
    },
    scrollContent: {
        paddingRight: theme.spacing.page,
    },
    noteCard: {
        minWidth: 140,
        paddingHorizontal: theme.spacing.standard.md,
        paddingVertical: theme.spacing.standard.sm,
        backgroundColor: theme.palette.background.subtle,
        borderRadius: theme.borderRadius.standard.md,
    },
}));
export default TopicNotesBanner;
